/**
 * Hitbox geometry and counter-square classes (owner: contact team). Hitboxes only decide CONTACT; WHO WINS is
 * the COUNTER matrix (tuning.ts), applied by resolve.ts. All boxes are axis-aligned ellipses; an overlap test
 * between two ellipses uses their Minkowski-sum approximation (radii added), padded by MOTION.contactSlop.
 * Exported for the AI (it may ask "would this dive reach?") and for tests.
 */
import { CarrierClass, HITBOX, LungeClass, MOTION } from '@hf/shared';
import { PlayerState } from '../enums';
import {
  diveActive,
  leapActive,
  spinInvulnerable,
  stiffArmActive,
  truckBursting,
  truckSquashed,
} from '../player/frames';
import { PF, lungeOpen } from '../player/fsm';
import { effectiveHeadRadius } from '../specials/query';
import type { PlayerSim } from '../state';

/** A target hurtbox (written by `hurtboxOf`). */
export interface Box {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}
export const hurt: Box = { cx: 0.5, cy: 0.5, rx: 0.5, ry: 0.5 };
export const lungeBox: Box = { cx: 0.5, cy: 0.5, rx: 0.5, ry: 0.5 };

/** The player's hurtbox: the head circle, squashed onto the ground while trucking (HITBOX.truck). */
export const hurtboxOf = (p: PlayerSim, out: Box): Box => {
  const r = effectiveHeadRadius(p);
  if (truckSquashed(p)) {
    const ry = r * HITBOX.truck.hurtboxScaleY;
    out.cx = p.x;
    out.cy = ry;
    out.rx = r;
    out.ry = ry;
  } else {
    out.cx = p.x;
    out.cy = p.y;
    out.rx = r;
    out.ry = r;
  }
  return out;
};

/** Generic hurtbox of a head at (x, y) with radius r (Mirage decoys). */
export const circleBox = (x: number, y: number, r: number, out: Box): Box => {
  out.cx = x;
  out.cy = y;
  out.rx = r;
  out.ry = r;
  return out;
};

/** Ellipse overlap (Minkowski approximation) with contact slop. */
export const boxesTouch = (a: Box, b: Box): boolean => {
  const dx = (a.cx - b.cx) / (a.rx + b.rx + MOTION.contactSlop);
  const dy = (a.cy - b.cy) / (a.ry + b.ry + MOTION.contactSlop);
  return dx * dx + dy * dy <= 1;
};

/** The attacker's lunge hitbox for `lunge` (DIVE / LEAP), in world space, scaled by the TKL stat. */
export const lungeBoxOf = (p: PlayerSim, lunge: LungeClass, out: Box): Box => {
  const h = lunge === LungeClass.DIVE ? HITBOX.dive : HITBOX.leap;
  const k = p.stats.tackleMul;
  out.cx = p.x + p.facing * h.ox;
  out.cy = p.y + h.oy;
  out.rx = h.rx * k;
  out.ry = h.ry * k;
  return out;
};

/** The stiff-arm Fist hitbox in world space. */
export const stiffArmBoxOf = (p: PlayerSim, out: Box): Box => {
  const h = HITBOX.stiffArm;
  out.cx = p.x + p.facing * h.ox;
  out.cy = p.y + h.oy;
  out.rx = h.rx;
  out.ry = h.ry;
  return out;
};

/** The live lunge class of `p` (DIVE / LEAP active and not yet resolved), or -1. */
export const activeLunge = (p: PlayerSim): number => {
  if (!lungeOpen(p)) return -1;
  if (diveActive(p)) return LungeClass.DIVE;
  if (leapActive(p)) return LungeClass.LEAP;
  return -1;
};

/** Height filters of the counter square: Dive hits LOW targets, Leap hits HIGH targets. */
export const lungeReachesHeight = (lunge: LungeClass, target: Box): boolean =>
  lunge === LungeClass.DIVE
    ? target.cy - target.ry < HITBOX.dive.maxTargetY
    : target.cy + target.ry > HITBOX.leap.minTargetY;

/**
 * May `d` make a plain contact GRAB? Grounded and in a neutral / moving state — not recovering, stunned, lunging,
 * landing from a whiffed leap, or BEATEN (`beatenFrames`) by the carrier's last counter win
 * (HITBOX.contactTackleEnabled).
 */
export const canGrab = (d: PlayerSim): boolean => {
  // A defender beaten by a counter-square win cannot grab that carrier again until the carrier is past him.
  if (!HITBOX.contactTackleEnabled || !d.grounded || d.beatenFrames > 0) return false;
  switch (d.state) {
    case PlayerState.IDLE:
    case PlayerState.RUN:
    case PlayerState.DASH:
    case PlayerState.BACKSTEP:
    case PlayerState.CATCH:
      return true;
    case PlayerState.LANDING:
      return (d.flags & PF.LUNGE_WHIFF) === 0;
    default:
      return false;
  }
};

/** Carrier class at the moment of contact (first matching row of the COUNTER matrix). */
export const carrierClassOf = (c: PlayerSim): CarrierClass => {
  if (spinInvulnerable(c)) return CarrierClass.SPIN;
  if (!c.grounded) return CarrierClass.AIR;
  if (truckBursting(c)) return CarrierClass.TRUCK_BURST;
  if (c.state === PlayerState.TRUCK_CHARGE) return CarrierClass.TRUCK_CHARGE;
  if (stiffArmActive(c)) return CarrierClass.STIFF_ARM;
  return CarrierClass.GROUND;
};

/**
 * Carrier class against THIS contact: like `carrierClassOf`, but a stiff-arm Fist or a truck burst only beats a
 * contact GRAB coming from in FRONT of the carrier (the side he faces). A defender grabbing from behind gets the
 * GROUND row (a tackle): a move aimed the other way does not punish a chasing defender.
 */
export const carrierClassAgainst = (c: PlayerSim, d: PlayerSim, lunge: LungeClass): CarrierClass => {
  const cc = carrierClassOf(c);
  if (
    lunge === LungeClass.GRAB &&
    (cc === CarrierClass.STIFF_ARM || cc === CarrierClass.TRUCK_BURST) &&
    (d.x - c.x) * c.facing <= 0
  )
    return CarrierClass.GROUND;
  return cc;
};
